import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Search, Loader2 } from "lucide-react";
import axios from "axios";
import "@/styles/commandDeck.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PendingApproval = ({ user }) => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  // Check if user is already approved by fetching fresh data from database
  useEffect(() => {
    const checkApprovalStatus = async () => {
      if (!user?.id) {
        setChecking(false);
        return;
      }

      try {
        // Fetch fresh user data from database
        const response = await axios.get(`${API}/users/${user.id}`);
        const dbUser = response.data;
        const dbStatus = (dbUser.status || '').toUpperCase();

        // If user is approved, update localStorage and redirect to dashboard
        if (dbStatus === 'APPROVED' || dbUser.is_approved) {
          // Update localStorage with fresh data
          const updatedUser = { ...user, ...dbUser, status: dbStatus };
          localStorage.setItem('shootzu_user', JSON.stringify(updatedUser));
          navigate("/dashboard");
          return;
        }
      } catch (error) {
        console.error('Error checking approval status:', error);
      }

      setChecking(false);
    };

    checkApprovalStatus();
  }, [user, navigate]);

  const firstName =
    (user?.name || user?.first_name || user?.full_name || "")
      .toString()
      .trim()
      .split(" ")[0];

  // Watermark + bloom shell shared by both states
  const Shell = ({ children }) => (
    <div className="cd-root" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
      <div className="cd-bloom-wrap">
        <div className="cd-bloom cd-bloom-1" />
        <div className="cd-bloom cd-bloom-2" />
      </div>
      <img
        src="/assets/shootzu-icon.png"
        alt=""
        aria-hidden="true"
        style={{
          position: "absolute",
          right: "-90px",
          top: "50%",
          transform: "translateY(-50%)",
          width: "460px",
          height: "460px",
          opacity: 0.05,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: "520px" }}>
        {children}
      </div>
    </div>
  );

  // Show loading while checking approval status
  if (checking) {
    return (
      <Shell>
        <div style={{ textAlign: "center" }}>
          <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: "#28C0D0" }} />
          <p style={{ color: "rgba(255,255,255,.6)", fontFamily: "var(--sz-font-body)" }}>
            Checking approval status...
          </p>
        </div>
      </Shell>
    );
  }

  const steps = [
    {
      n: "1",
      accentBg: "rgba(40,192,208,.16)",
      accent: "#28C0D0",
      title: "We check your ABN & details",
      sub: "Every member is vetted — it keeps the network trusted.",
    },
    {
      n: "2",
      accentBg: "rgba(103,198,180,.16)",
      accent: "#67C6B4",
      title: "You get the green light by email",
      sub: (
        <>
          We&rsquo;ll email you at{" "}
          <span style={{ color: "#fff", fontWeight: 600 }}>{user?.email}</span>.
        </>
      ),
    },
    {
      n: "3",
      accentBg: "rgba(120,40,124,.2)",
      accent: "#C98AD4",
      title: "Start applying & getting hired",
      sub: "Your next gig is one tap away.",
    },
  ];

  return (
    <Shell>
      <div
        style={{
          background: "rgba(18,22,38,.74)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          border: "1px solid rgba(255,255,255,.12)",
          borderRadius: "24px",
          boxShadow: "0 40px 90px -30px rgba(0,0,0,.7)",
          padding: "38px 36px",
          textAlign: "center",
        }}
      >
        <img
          src="/assets/shootzu-logo-white.png"
          alt="Shootzu"
          style={{ height: "36px", width: "auto", marginBottom: "28px" }}
        />

        {/* Brand glyph */}
        <div
          style={{
            width: "80px",
            height: "80px",
            borderRadius: "22px",
            margin: "0 auto",
            background: "rgba(180,83,9,.16)",
            border: "1px solid rgba(180,83,9,.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FCD34D",
          }}
        >
          <Clock style={{ width: "38px", height: "38px" }} strokeWidth={2} />
        </div>

        {/* Title */}
        <h1
          style={{
            fontFamily: "var(--sz-font-display)",
            fontWeight: 800,
            fontSize: "26px",
            color: "#fff",
            letterSpacing: "-.6px",
            margin: "24px 0 0",
          }}
        >
          Profile under review
        </h1>
        <p
          style={{
            fontSize: "14px",
            color: "rgba(255,255,255,.6)",
            lineHeight: 1.6,
            margin: "10px 0 0",
          }}
        >
          Thanks{firstName ? `, ${firstName}` : ""} — our team is vetting your profile now. We
          usually approve crew within{" "}
          <span style={{ color: "#FCD34D", fontWeight: 600 }}>24–48 hours</span>.
        </p>

        {/* What happens next */}
        <div
          style={{
            textAlign: "left",
            background: "rgba(255,255,255,.04)",
            border: "1px solid rgba(255,255,255,.08)",
            borderRadius: "16px",
            padding: "20px 22px",
            marginTop: "24px",
          }}
        >
          <div
            style={{
              fontFamily: "var(--sz-font-display)",
              fontWeight: 700,
              fontSize: "13px",
              color: "#fff",
              marginBottom: "14px",
            }}
          >
            What happens next?
          </div>
          {steps.map((s, i) => (
            <div
              key={s.n}
              style={{
                display: "flex",
                gap: "13px",
                marginBottom: i < steps.length - 1 ? "14px" : 0,
              }}
            >
              <span
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "999px",
                  background: s.accentBg,
                  color: s.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "var(--sz-font-display)",
                  fontWeight: 700,
                  fontSize: "12px",
                  flex: "0 0 auto",
                }}
              >
                {s.n}
              </span>
              <div>
                <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#fff" }}>
                  {s.title}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "rgba(255,255,255,.5)",
                    marginTop: "2px",
                  }}
                >
                  {s.sub}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Browse Jobs */}
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "9px",
            width: "100%",
            marginTop: "24px",
            padding: "14px",
            borderRadius: "13px",
            background: "#28C0D0",
            border: "none",
            color: "#0C1120",
            fontFamily: "var(--sz-font-body)",
            fontWeight: 700,
            fontSize: "14px",
            cursor: "pointer",
            boxShadow: "0 12px 30px -12px rgba(40,192,208,.7)",
          }}
        >
          <Search style={{ width: "17px", height: "17px" }} strokeWidth={2.4} />
          Browse jobs while you wait
        </button>

        <div
          style={{
            fontSize: "11.5px",
            color: "rgba(255,255,255,.4)",
            marginTop: "16px",
          }}
        >
          Find Crew, Be Crew · Shootzu
        </div>
      </div>
    </Shell>
  );
};

export default PendingApproval;
